// src/lib/walkthrough/waitForElement.ts
//
// PURE, injectable "wait for a target to appear" logic extracted from
// WalkthroughPlayer.resolveElementWithObserver so the wait / observe / timeout /
// cleanup behavior is unit-guarded (docs/REGRESSIONS.md 2026-07-01) and can run
// under the node-env vitest gate (no real DOM).
//
// WHY IT MATTERS: several walkthrough steps target DYNAMIC elements that only
// render after a user action — the address-autocomplete suggestion rows, and the
// project-record owner-option rows that appear only once the SELECT OWNERS
// dropdown is opened. If the resolver ran ONCE at step activation those targets
// would never be caught. This helper re-checks on every observed DOM mutation and
// resolves as soon as the target appears, while keeping the caller's
// manual-continue dead-end fallback for the never-appears case.
//
// The player supplies the real `resolveNow` (resolveElement(step)) plus a
// MutationObserver-backed `observe` and a setTimeout-backed `schedule`; the test
// supplies fakes. Behavior contract:
//   - resolves IMMEDIATELY (sync-settled promise) if the target is already present;
//   - otherwise re-checks on every observed mutation, resolving the instant the
//     target appears;
//   - resolves `null` after `timeoutMs` (caller keeps its dead-end fallback);
//   - disconnects the observer + clears the timer EXACTLY ONCE (whichever path
//     wins), so there is no leak and no double-resolve;
//   - `registerCancel` lets an early back/advance/exit tear the wait down and
//     settle it as `null`.

export interface WaitForElementDeps<T> {
  /** Resolve the target now, or null if it isn't present yet. */
  resolveNow: () => T | null
  /** Start observing; call `onMutation` on each change. Returns a disconnect fn. */
  observe: (onMutation: () => void) => () => void
  /** Schedule `cb` after `ms`. Returns a clear fn. */
  schedule: (cb: () => void, ms: number) => () => void
  /** How long to wait before giving up (resolving null). */
  timeoutMs: number
  /** Register a cancel fn the caller can invoke to abort the wait (settles null). */
  registerCancel: (cancel: () => void) => void
}

export function waitForElement<T>(deps: WaitForElementDeps<T>): Promise<T | null> {
  // Fast path: already present → no observer/timer needed.
  const immediate = deps.resolveNow()
  if (immediate != null) return Promise.resolve(immediate)

  return new Promise<T | null>(resolve => {
    let done = false
    let disconnect: (() => void) | null = null
    let clearTimer: (() => void) | null = null
    const finish = (el: T | null) => {
      if (done) return
      done = true
      if (disconnect) { try { disconnect() } catch { /* */ } disconnect = null }
      if (clearTimer) { try { clearTimer() } catch { /* */ } clearTimer = null }
      resolve(el)
    }
    // Re-check on each observed mutation; resolve the moment the target appears.
    disconnect = deps.observe(() => {
      const el = deps.resolveNow()
      if (el != null) finish(el)
    })
    // Give-up timer → null (caller keeps its manual-continue fallback).
    clearTimer = deps.schedule(() => finish(null), deps.timeoutMs)
    // External cancel (back / advance / exit before the target appears).
    deps.registerCancel(() => finish(null))
  })
}
